import React, { useEffect, useState } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { WebSocketProvider, useWebSocket } from './websocket/WebSocketContext';
import { Wifi } from 'lucide-react';

// Loading component that shows while WebSocket connects
function WebSocketLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] flex items-center justify-center">
      <div className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-12 max-w-md w-full mx-4">
        <div className="flex flex-col items-center space-y-6">
          {/* Animated icon */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Wifi className="w-10 h-10 text-white animate-pulse" />
            </div>
          </div>

          {/* Loading text */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-white">Connecting...</h2>
            <p className="text-white/60">Establishing secure connection</p>
          </div>

          {/* Animated dots */}
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper component to access WebSocket context
function DashboardWrapper({ email, password, onLogout }) {
  const { connected, deliveryMode } = useWebSocket();

  // Only show loader if deliveryMode is 'socket' and not connected
  if (deliveryMode === 'socket' && !connected) {
    return <WebSocketLoader />;
  }

  return <Dashboard email={email} password={password} onLogout={onLogout} />;
}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Load saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("loggedInEmail");
    const savedPassword = localStorage.getItem("loggedInPassword");

    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setLoggedIn(true);
    }
  }, []);

  const handleLogin = (email, password) => {
    localStorage.setItem("loggedInEmail", email);
    localStorage.setItem("loggedInPassword", password);

    setEmail(email);
    setPassword(password);
    setLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("loggedInEmail");
    localStorage.removeItem("loggedInPassword");

    setEmail("");
    setPassword("");
    setLoggedIn(false);
  };

  return (
    <WebSocketProvider loginId={loggedIn ? email : null}>
      {loggedIn ? (
        <DashboardWrapper
          email={email}
          password={password}
          onLogout={handleLogout}
        />
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </WebSocketProvider>
  );
}

export default App;